import { useEffect, useState, useRef } from "react";
import {
  getAnchoredContentSourceText,
  type AnchoredSourceResult,
} from "@/lib/content/getContentSourceText";
import type { ContentItem } from "@/types/content";

const MAX_TOTAL_CHARS = 50000;

interface RoomContentTextResult {
  contentText: string | null;
  isLoading: boolean;
  groundedCount: number;
  hasPageAnchors: boolean;
  hasTimeAnchors: boolean;
  /** True when the prompt embeds multiple distinct documents (`[doc:UUID]` headers). */
  isMultiDoc: boolean;
}

/**
 * Aggregates anchored document text and media transcripts for all content
 * items in a room. Each section is prefixed with `[doc:UUID]` so the model
 * can disambiguate citations across multiple sources.
 *
 * Output shape per source:
 *   ### Title
 *   [doc:UUID]
 *   <content>
 *   [Page 1]
 *   ...page text...
 *
 *   [Page 2]
 *   ...
 *   </content>
 */
export function useRoomContentText(contents: ContentItem[]): RoomContentTextResult {
  const [contentText, setContentText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [groundedCount, setGroundedCount] = useState(0);
  const [hasPageAnchors, setHasPageAnchors] = useState(false);
  const [hasTimeAnchors, setHasTimeAnchors] = useState(false);
  const [isMultiDoc, setIsMultiDoc] = useState(false);

  // Cache key based on sorted content IDs
  const cacheKeyRef = useRef<string>("");
  const cachedRef = useRef<{
    text: string | null;
    count: number;
    hasPageAnchors: boolean;
    hasTimeAnchors: boolean;
    isMultiDoc: boolean;
  } | null>(null);

  useEffect(() => {
    if (!contents.length) {
      setContentText(null);
      setGroundedCount(0);
      setHasPageAnchors(false);
      setHasTimeAnchors(false);
      setIsMultiDoc(false);
      setIsLoading(false);
      return;
    }

    const newCacheKey = contents.map((c) => c.id).sort().join(",");
    if (newCacheKey === cacheKeyRef.current && cachedRef.current) {
      const c = cachedRef.current;
      setContentText(c.text);
      setGroundedCount(c.count);
      setHasPageAnchors(c.hasPageAnchors);
      setHasTimeAnchors(c.hasTimeAnchors);
      setIsMultiDoc(c.isMultiDoc);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchAll = async () => {
      setIsLoading(true);

      try {
        // Fetch all anchored content texts in parallel
        const results = await Promise.all(
          contents.map(async (item) => {
            const anchored = await getAnchoredContentSourceText(item.id, item.type);
            return { id: item.id, title: item.title, anchored };
          })
        );

        if (cancelled) return;

        const withText = results.filter(
          (r): r is { id: string; title: string; anchored: AnchoredSourceResult } =>
            !!r.anchored
        );
        if (!withText.length) {
          cacheKeyRef.current = newCacheKey;
          cachedRef.current = {
            text: null,
            count: 0,
            hasPageAnchors: false,
            hasTimeAnchors: false,
            isMultiDoc: false,
          };
          setContentText(null);
          setGroundedCount(0);
          setHasPageAnchors(false);
          setHasTimeAnchors(false);
          setIsMultiDoc(false);
          return;
        }

        let aggregated = "";
        let count = 0;
        let anyPage = false;
        let anyTime = false;

        for (const item of withText) {
          const header = `### ${item.title}\n[doc:${item.id}]\n<content>\n`;
          const footer = `\n</content>\n\n`;
          const body = item.anchored.text;
          const sectionLen = header.length + body.length + footer.length;

          if (aggregated.length + sectionLen > MAX_TOTAL_CHARS) {
            const remaining = MAX_TOTAL_CHARS - aggregated.length - header.length - footer.length - 16;
            if (remaining > 200) {
              aggregated += `${header}${body.slice(0, remaining)}\n[truncated]${footer}`;
              count++;
              if (item.anchored.hasPageAnchors) anyPage = true;
              if (item.anchored.hasTimeAnchors) anyTime = true;
            }
            break;
          }
          aggregated += `${header}${body}${footer}`;
          count++;
          if (item.anchored.hasPageAnchors) anyPage = true;
          if (item.anchored.hasTimeAnchors) anyTime = true;
        }

        const multi = count > 1;
        cacheKeyRef.current = newCacheKey;
        cachedRef.current = {
          text: aggregated,
          count,
          hasPageAnchors: anyPage,
          hasTimeAnchors: anyTime,
          isMultiDoc: multi,
        };
        setContentText(aggregated);
        setGroundedCount(count);
        setHasPageAnchors(anyPage);
        setHasTimeAnchors(anyTime);
        setIsMultiDoc(multi);
      } catch (error) {
        console.error("Error aggregating room content text:", error);
        if (!cancelled) {
          setContentText(null);
          setGroundedCount(0);
          setHasPageAnchors(false);
          setHasTimeAnchors(false);
          setIsMultiDoc(false);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [contents]);

  return {
    contentText,
    isLoading,
    groundedCount,
    hasPageAnchors,
    hasTimeAnchors,
    isMultiDoc,
  };
}
